import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageHeader from "../karkhana/ui/PageHeader";
import notifications from "../../common/Components/Notifications/Notification";
import {
  ApiResult,
  OrderUpdate,
  ProductionOrderView,
  STAGES,
  acceptTerms,
  cancelOrder,
  counterOffer,
  declineOrder,
  designerView,
  editProductionOrder,
  saveDispatch,
  getProductionOrder,
  incomingOrder,
  inr,
  postUpdate,
  presignFile,
  revokeLink,
  savePrivate,
  saveSchedule,
  setVisibility,
  shortDate,
  stageLabel,
  uploadTo,
} from "../../services/partnerNetworkApi";
import { DesignFiles, HealthDot, STATUS_LABEL, ShownRef, Timeline } from "./pnUi";
import "../karkhana/ui/karkhana-ui.scss";
import "./partner-network.scss";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** One page for both sides. The server decides what each side gets; this only lays it out. */
const ProductionOrderPage = ({ side }: { side: "PRINCIPAL" | "PARTNER" }) => {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<ProductionOrderView | null>(null);
  const [preview, setPreview] = useState<ProductionOrderView | null>(null);
  const [tab, setTab] = useState<"ours" | "theirs">("ours");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    const res = side === "PRINCIPAL" ? await getProductionOrder(id) : await incomingOrder(id);
    if (res.success) setOrder(res.data);
    else setError(res.message || "Could not open this order");
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, side]);

  useEffect(() => {
    if (tab === "theirs" && side === "PARTNER") {
      designerView(id).then((res) => res.success && setPreview(res.data));
    }
  }, [tab, id, side, order]);

  const run = async (call: () => Promise<ApiResult<ProductionOrderView>>, done?: string) => {
    setBusy(true);
    const res = await call();
    setBusy(false);
    if (res.success && res.data) {
      setOrder(res.data);
      if (done) notifications({ title: order?.code || "Order", message: done, type: "success" });
      return true;
    }
    notifications({ title: order?.code || "Order", message: res.message, type: "danger" });
    return false;
  };

  if (error) {
    return (
      <div className="pn-page">
        <PageHeader title="Production order" back={side === "PRINCIPAL" ? "/partner-network/board" : "/partner-network/incoming"} />
        <div className="pn-card"><p>{error}</p></div>
      </div>
    );
  }
  if (!order) {
    return <div className="pn-page"><p className="pn-muted">Loading…</p></div>;
  }

  const other = side === "PRINCIPAL" ? order.partner?.name || "the partner" : order.designer || "the designer";
  const negotiating = order.status === "SENT" || order.status === "COUNTERED";
  const myTurn = negotiating && order.turn === side && !order.readOnly;
  const accepted = order.status === "ACCEPTED";

  return (
    <div className="pn-page">
      <PageHeader title={`${order.code} · ${order.title}`} back={side === "PRINCIPAL" ? "/partner-network/board" : "/partner-network/incoming"}>
        <HealthDot health={order.health} withLabel />
      </PageHeader>
      {order.readOnly ? <p className="pn-note">This partnership has ended. The shared history is read-only.</p> : null}

      {side === "PARTNER" ? (
        <div className="pn-tabs" role="tablist">
          <button role="tab" aria-selected={tab === "ours"} className={tab === "ours" ? "is-on" : ""} onClick={() => setTab("ours")}>
            Our view
          </button>
          <button role="tab" aria-selected={tab === "theirs"} className={tab === "theirs" ? "is-on" : ""} onClick={() => setTab("theirs")}>
            What {other} sees
          </button>
        </div>
      ) : null}

      {side === "PARTNER" && tab === "theirs" ? (
        <div className="pn-card">
          <h3>Exactly what {other} sees on this order</h3>
          {preview ? (
            <>
              <OrderFacts order={preview} />
              <h3 style={{ marginTop: 16 }}>Timeline</h3>
              <Timeline updates={preview.updates} />
            </>
          ) : (
            <p className="pn-muted">Loading…</p>
          )}
        </div>
      ) : (
        <div className="pn-order">
          <div>
            <div className="pn-card">
              <h3>Order</h3>
              <OrderFacts order={order} />
              {order.specifications ? <p style={{ marginTop: 12 }}>{order.specifications}</p> : null}
              {order.lines?.length ? (
                <ul className="pn-list" style={{ marginTop: 8 }}>
                  {order.lines.map((l, i) => (
                    <li key={i}>
                      <span>{[l.colour, l.size].filter(Boolean).join(" · ") || "—"}</span>
                      <b>{l.quantity ?? "—"}</b>
                    </li>
                  ))}
                </ul>
              ) : null}
              {order.notes ? <p className="pn-muted">Note: {order.notes}</p> : null}
              {side === "PRINCIPAL" && negotiating && !order.readOnly ? (
                <DesignFilesEditor order={order} busy={busy} run={run} />
              ) : (
                <DesignFiles refs={order.designRefs || []} />
              )}
              {order.dispatch ? <DispatchSummary dispatch={order.dispatch} /> : null}
            </div>

            {side === "PARTNER" && accepted && !order.readOnly ? <DispatchCard order={order} busy={busy} run={run} /> : null}

            <Negotiation order={order} side={side} myTurn={myTurn} busy={busy} other={other}
              onAccept={() => run(() => acceptTerms(order.id), "Terms agreed")}
              onCounter={(body) => run(() => counterOffer(order.id, body), "Counter-offer sent")}
              onDecline={() => run(() => declineOrder(order.id), "Order declined")} />

            {accepted || order.updates.length ? (
              <div className="pn-card">
                <h3>Timeline</h3>
                {side === "PARTNER" ? (
                  <p className="pn-note">
                    You choose what {other} sees. Turning an item off hides it from {other} from now on. Already-downloaded files
                    can't be recalled.
                  </p>
                ) : null}
                {accepted && !order.readOnly ? <Composer order={order} side={side} busy={busy} other={other} run={run} /> : null}
                <Timeline
                  updates={order.updates}
                  shareWith={other}
                  busy={busy}
                  onToggle={(u: OrderUpdate, share: boolean) =>
                    run(() => setVisibility(order.id, u.id, share ? "SHARED" : "INTERNAL"), share ? `Shared with ${other}` : "Now internal")
                  }
                />
              </div>
            ) : null}
          </div>

          <aside>
            {side === "PRINCIPAL" ? (
              <>
                <div className="pn-card">
                  <h3>Partner</h3>
                  <p style={{ margin: 0 }}><b>{order.partner?.name}</b></p>
                  <p className="pn-muted">{order.partner?.mode === "LINK_ONLY" ? "Link only — updates belong to you" : order.partner?.mode === "SPONSORED" ? "Sponsored workspace" : "On HKP"}</p>
                  {order.partner?.mode === "LINK_ONLY" && !order.readOnly ? (
                    <button className="kh-btn" disabled={busy} onClick={async () => {
                      if (!window.confirm("Switch off this order's link? The partner won't be able to open it.")) return;
                      const res = await revokeLink(order.id);
                      notifications({ title: order.code, message: res.success ? "Link switched off" : res.message, type: res.success ? "success" : "danger" });
                    }}>Switch off link</button>
                  ) : null}
                </div>
                {accepted && !order.readOnly ? <ScheduleCard order={order} busy={busy} run={run} /> : null}
                <PrivateCard order={order} busy={busy} run={run} />
                {!order.readOnly && (negotiating || accepted) && order.stage !== "COMPLETED" ? (
                  <button className="kh-btn" disabled={busy} onClick={() => window.confirm("Cancel this order?") && run(() => cancelOrder(order.id), "Order cancelled")}>
                    Cancel order
                  </button>
                ) : null}
              </>
            ) : (
              <div className="pn-card">
                <h3>From {other}</h3>
                {order.linkedStudioOrderId ? (
                  <p><Link to={`/orders/${order.linkedStudioOrderId}`}>Open our internal order</Link></p>
                ) : null}
                <p className="pn-muted">Your workers, wages, costs and other clients are never shared.</p>
                <Link className="kh-btn" to="/partner-network/incoming?tab=partners">Share settings</Link>
              </div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
};

const DesignFilesEditor = ({ order, busy, run }: {
  order: ProductionOrderView; busy: boolean;
  run: (call: () => Promise<ApiResult<ProductionOrderView>>, done?: string) => Promise<boolean>;
}) => {
  const [refs, setRefs] = useState<ShownRef[]>(order.designRefs || []);
  const changed = JSON.stringify(refs.map((r) => r.key)) !== JSON.stringify((order.designRefs || []).map((r) => r.key));
  return (
    <div style={{ marginTop: 12 }}>
      <h3 style={{ fontSize: 14, margin: 0 }}>Design files</h3>
      <DesignFiles refs={refs} onChange={setRefs} />
      {changed ? (
        <div className="pn-actions">
          <button className="kh-btn is-primary" disabled={busy} onClick={() => run(() => editProductionOrder(order.id, {
            // PUT replaces the spec: send it all back with the new file list.
            title: order.title, productType: order.productType, specifications: order.specifications,
            lines: order.lines, dueDate: order.dueDate, proposedRate: order.proposedRate, quantity: order.quantity,
            notes: order.notes, designRefs: refs.map((r) => ({ key: r.key, name: r.name, contentType: r.contentType })),
          }), "Design files saved")}>Save design files</button>
          <button className="kh-btn" disabled={busy} onClick={() => setRefs(order.designRefs || [])}>Undo</button>
        </div>
      ) : null}
    </div>
  );
};

const DispatchSummary = ({ dispatch }: { dispatch: NonNullable<ProductionOrderView["dispatch"]> }) => (
  <p className="pn-note" style={{ marginTop: 12, background: "#eef8f0", color: "#1d5a2e" }}>
    Dispatched {shortDate(dispatch.date)}{dispatch.pieces ? ` · ${dispatch.pieces} pieces` : ""}
    {dispatch.courier ? ` · ${dispatch.courier}` : ""}{dispatch.tracking ? ` · tracking ${dispatch.tracking}` : ""}
  </p>
);

/** Partner side: record what was sent. The designer sees it on the order. */
const DispatchCard = ({ order, busy, run }: {
  order: ProductionOrderView; busy: boolean;
  run: (call: () => Promise<ApiResult<ProductionOrderView>>, done?: string) => Promise<boolean>;
}) => {
  const d = order.dispatch || {};
  const [date, setDate] = useState(d.date ? new Date(d.date).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10));
  const [courier, setCourier] = useState(d.courier || "");
  const [tracking, setTracking] = useState(d.tracking || "");
  const [pieces, setPieces] = useState(String(d.pieces ?? order.piecesDone ?? ""));
  return (
    <div className="pn-card">
      <h3>Dispatch</h3>
      <p className="pn-muted">Shared with {order.designer || "the designer"} when you save.</p>
      <div className="pn-form">
        <label>Date<input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        <label>Pieces<input inputMode="numeric" value={pieces} onChange={(e) => setPieces(e.target.value.replace(/\D/g, ""))} /></label>
        <label>Courier<input value={courier} onChange={(e) => setCourier(e.target.value)} placeholder="Delhivery, DTDC, by hand…" /></label>
        <label>Tracking no.<input value={tracking} onChange={(e) => setTracking(e.target.value)} /></label>
      </div>
      <div className="pn-actions">
        <button className="kh-btn is-primary" disabled={busy || !date} onClick={() => run(() => saveDispatch(order.id, {
          date, courier: courier.trim() || undefined, tracking: tracking.trim() || undefined,
          pieces: pieces ? Number(pieces) : undefined,
        }), "Dispatch saved")}>{order.dispatch ? "Update dispatch" : "Mark dispatched"}</button>
      </div>
    </div>
  );
};

const OrderFacts = ({ order }: { order: ProductionOrderView }) => {
  const last = order.offers[order.offers.length - 1];
  return (
    <dl className="pn-facts">
      <div>
        <dt>{order.termsFrozen ? "Agreed rate" : "Rate / piece"}</dt>
        <dd>{inr(order.agreedRate ?? last?.rate ?? order.proposedRate)}</dd>
      </div>
      <div>
        <dt>Pieces</dt>
        <dd>{order.status === "ACCEPTED" ? `${order.piecesDone ?? 0} / ${order.agreedQuantity ?? "—"}` : last?.quantity ?? order.quantity ?? "—"}</dd>
      </div>
      <div>
        <dt>Due</dt>
        <dd>{shortDate(order.agreedDueDate ?? last?.dueDate ?? order.dueDate)}</dd>
      </div>
      <div>
        <dt>Stage</dt>
        <dd>{order.status === "ACCEPTED" ? stageLabel(order.stage) : STATUS_LABEL[order.status] || order.status}</dd>
      </div>
    </dl>
  );
};

const Negotiation = ({
  order, side, myTurn, busy, other, onAccept, onCounter, onDecline,
}: {
  order: ProductionOrderView; side: string; myTurn: boolean; busy: boolean; other: string;
  onAccept: () => void; onCounter: (body: any) => Promise<boolean>; onDecline: () => void;
}) => {
  const last = order.offers[order.offers.length - 1];
  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState(String(last?.rate ?? ""));
  const [qty, setQty] = useState(String(last?.quantity ?? ""));
  const [due, setDue] = useState(last?.dueDate ? new Date(last.dueDate).toISOString().slice(0, 10) : "");
  const [message, setMessage] = useState("");
  return (
    <div className="pn-card">
      <h3>{order.termsFrozen ? "Agreed terms" : "Negotiation"}</h3>
      <ul className="pn-offers">
        {order.offers.map((o, i) => (
          <li key={i} className={o.by === side ? "is-mine" : ""}>
            <b>{o.by === side ? "You" : o.byName || other}</b> · {shortDate(o.at)}
            <div>
              {o.rate !== undefined && o.rate !== null ? `${inr(o.rate)} / piece` : ""}
              {o.quantity ? ` · ${o.quantity} pcs` : ""}
              {o.dueDate ? ` · by ${shortDate(o.dueDate)}` : ""}
            </div>
            {o.message ? <div className="pn-muted">{o.message}</div> : null}
          </li>
        ))}
      </ul>
      {order.termsFrozen ? (
        <p className="pn-note">Agreed {inr(order.agreedRate)} / piece for {order.agreedQuantity} pieces by {shortDate(order.agreedDueDate)}. Both sides see these terms; they can't be changed.</p>
      ) : null}
      {myTurn ? (
        <>
          <div className="pn-actions">
            <button className="kh-btn is-primary" disabled={busy} onClick={onAccept}>Accept terms</button>
            <button className="kh-btn" disabled={busy} onClick={() => setOpen(!open)}>Counter</button>
            {side === "PARTNER" ? <button className="kh-btn" disabled={busy} onClick={() => window.confirm("Decline this order?") && onDecline()}>Decline</button> : null}
          </div>
          {open ? (
            <div className="pn-form" style={{ marginTop: 12 }}>
              <label>Rate / piece (₹)<input inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} /></label>
              <label>Quantity<input inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)} /></label>
              <label>Date<input type="date" value={due} onChange={(e) => setDue(e.target.value)} /></label>
              <label className="is-wide">Message<input value={message} onChange={(e) => setMessage(e.target.value)} /></label>
              <div className="is-wide">
                <button className="kh-btn is-primary" disabled={busy} onClick={async () => {
                  if (await onCounter({ rate: rate ? Number(rate) : undefined, quantity: qty ? Number(qty) : undefined, dueDate: due || undefined, message })) setOpen(false);
                }}>Send counter</button>
              </div>
            </div>
          ) : null}
        </>
      ) : !order.termsFrozen && (order.status === "SENT" || order.status === "COUNTERED") ? (
        <p className="pn-muted">Waiting for {other} to reply.</p>
      ) : null}
    </div>
  );
};

const Composer = ({ order, side, busy, other, run }: {
  order: ProductionOrderView; side: string; busy: boolean; other: string;
  run: (call: () => Promise<ApiResult<ProductionOrderView>>, done?: string) => Promise<boolean>;
}) => {
  const [text, setText] = useState("");
  const [share, setShare] = useState(false);
  const [keepInternal, setKeepInternal] = useState(false);
  const [pieces, setPieces] = useState(String(order.piecesDone ?? 0));
  const photo = useRef<HTMLInputElement | null>(null);
  const policy = order.sharePolicy || {};
  const askPhotos = side === "PARTNER" && policy.PHOTOS === "ASK";

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    const refs: { key: string; name: string; contentType: string }[] = [];
    for (const file of Array.from(files)) {
      const signed = await presignFile(order.id, file.name, file.type || "image/jpeg");
      if (!signed.success || !(await uploadTo(signed.data.uploadUrl, file, signed.data.contentType))) {
        notifications({ title: order.code, message: signed.message || "Upload failed", type: "danger" });
        return;
      }
      refs.push({ key: signed.data.key, name: file.name, contentType: signed.data.contentType });
    }
    run(() => postUpdate(order.id, { kind: "PHOTO", files: refs, share }), "Photos added");
  };

  if (side === "PRINCIPAL") {
    return (
      <div className="pn-form" style={{ marginBottom: 12 }}>
        <label className="is-wide">Note to {other}<input value={text} onChange={(e) => setText(e.target.value)} /></label>
        <div className="is-wide">
          <button className="kh-btn" disabled={busy || !text.trim()} onClick={async () => {
            if (await run(() => postUpdate(order.id, { kind: "NOTE", text }), "Note sent")) setText("");
          }}>Send note</button>
        </div>
      </div>
    );
  }
  return (
    <div style={{ marginBottom: 12 }}>
      <div className="pn-actions" style={{ marginTop: 0 }}>
        {STAGES.filter((s) => s !== "ACCEPTED").map((s) => (
          <button key={s} className={`kh-btn${order.stage === s ? " is-primary" : ""}`} disabled={busy}
            onClick={() => run(() => postUpdate(order.id, { kind: "STAGE", stage: s }), `Stage: ${stageLabel(s)}`)}>
            {stageLabel(s)}
          </button>
        ))}
      </div>
      <div className="pn-form" style={{ marginTop: 10 }}>
        <label>Pieces done<input inputMode="numeric" value={pieces} onChange={(e) => setPieces(e.target.value)} /></label>
        <label className="is-wide">Note<input value={text} onChange={(e) => setText(e.target.value)} /></label>
        <label className="is-wide" style={{ flexDirection: "row", alignItems: "center" }}>
          <input type="checkbox" style={{ minHeight: 0 }} checked={keepInternal} onChange={(e) => setKeepInternal(e.target.checked)} />
          Keep this note internal (not shared with {other})
        </label>
      </div>
      <div className="pn-actions">
        <button className="kh-btn" disabled={busy} onClick={() => run(() => postUpdate(order.id, { kind: "PROGRESS", piecesDone: Number(pieces) }), "Progress saved")}>Save pieces</button>
        <button className="kh-btn" disabled={busy || !text.trim()} onClick={async () => {
          if (await run(() => postUpdate(order.id, { kind: "NOTE", text, share: keepInternal ? false : undefined }), "Note saved")) setText("");
        }}>Add note</button>
        <button className="kh-btn" disabled={busy || !text.trim()} onClick={async () => {
          if (await run(() => postUpdate(order.id, { kind: "DELAY", text }), `${other} was told`)) setText("");
        }}>Report problem / delay</button>
        <button className="kh-btn" disabled={busy} onClick={() => photo.current?.click()}>Add photos</button>
        <input ref={photo} type="file" accept="image/*" multiple hidden onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
      </div>
      {askPhotos ? (
        <label className="pn-share">
          <input type="checkbox" role="switch" checked={share} onChange={(e) => setShare(e.target.checked)} />
          <span className="pn-switch" aria-hidden="true" />
          Share these photos with {other}?
        </label>
      ) : null}
    </div>
  );
};

const ScheduleCard = ({ order, busy, run }: {
  order: ProductionOrderView; busy: boolean;
  run: (call: () => Promise<ApiResult<ProductionOrderView>>, done?: string) => Promise<boolean>;
}) => {
  const s = order.updateSchedule || {};
  const [mode, setMode] = useState(s.weekdays?.length ? "weekdays" : "every");
  const [everyDays, setEveryDays] = useState(String(s.everyDays || 3));
  const [weekdays, setWeekdays] = useState<number[]>(s.weekdays || [1, 4]);
  const [time, setTime] = useState(s.timeOfDay || "10:00");
  const [followUp, setFollowUp] = useState(String(s.followUpAfterHours || 24));
  const [maxFollowUps, setMaxFollowUps] = useState(String(s.maxFollowUps ?? 2));
  const [channels, setChannels] = useState<string[]>(s.channels || ["WHATSAPP", "SMS"]);
  const [paused, setPaused] = useState(Boolean(s.paused));
  const toggle = <T,>(list: T[], v: T) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  return (
    <div className="pn-card">
      <h3>Update schedule</h3>
      <p className="pn-muted">The partner gets a WhatsApp / SMS asking for an update, and a reminder if they don't reply.</p>
      <div className="pn-tabs">
        <button className={mode === "every" ? "is-on" : ""} onClick={() => setMode("every")}>Every few days</button>
        <button className={mode === "weekdays" ? "is-on" : ""} onClick={() => setMode("weekdays")}>On weekdays</button>
      </div>
      <div className="pn-form">
        {mode === "every" ? (
          <label>Every (days)<input inputMode="numeric" value={everyDays} onChange={(e) => setEveryDays(e.target.value)} /></label>
        ) : (
          <div className="is-wide pn-actions" style={{ marginTop: 0 }}>
            {WEEKDAYS.map((d, i) => (
              <button key={d} className={`kh-btn${weekdays.includes(i + 1) ? " is-primary" : ""}`} onClick={() => setWeekdays(toggle(weekdays, i + 1))}>{d}</button>
            ))}
          </div>
        )}
        <label>At<input type="time" value={time} onChange={(e) => setTime(e.target.value)} /></label>
        <label>Remind after (hours)<input inputMode="numeric" value={followUp} onChange={(e) => setFollowUp(e.target.value)} /></label>
        <label>Reminders<input inputMode="numeric" value={maxFollowUps} onChange={(e) => setMaxFollowUps(e.target.value)} /></label>
        <label style={{ flexDirection: "row", alignItems: "center" }}><input type="checkbox" style={{ minHeight: 0 }} checked={channels.includes("WHATSAPP")} onChange={() => setChannels(toggle(channels, "WHATSAPP"))} /> WhatsApp</label>
        <label style={{ flexDirection: "row", alignItems: "center" }}><input type="checkbox" style={{ minHeight: 0 }} checked={channels.includes("SMS")} onChange={() => setChannels(toggle(channels, "SMS"))} /> SMS</label>
        <label className="is-wide" style={{ flexDirection: "row", alignItems: "center" }}><input type="checkbox" style={{ minHeight: 0 }} checked={paused} onChange={(e) => setPaused(e.target.checked)} /> Paused</label>
      </div>
      {order.nextNudgeAt ? <p className="pn-muted">Next request: {new Date(order.nextNudgeAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}. No messages 9 pm – 8 am.</p> : null}
      <button className="kh-btn is-primary" disabled={busy} onClick={() => run(() => saveSchedule(order.id, {
        everyDays: mode === "every" ? Number(everyDays) : null,
        weekdays: mode === "weekdays" ? weekdays : [],
        timeOfDay: time, timezone: "Asia/Kolkata",
        followUpAfterHours: Number(followUp), maxFollowUps: Number(maxFollowUps), channels, paused,
      }), "Schedule saved")}>Save schedule</button>
    </div>
  );
};

const PrivateCard = ({ order, busy, run }: {
  order: ProductionOrderView; busy: boolean;
  run: (call: () => Promise<ApiResult<ProductionOrderView>>, done?: string) => Promise<boolean>;
}) => {
  const p = order.principalPrivate || {};
  const [clientName, setClientName] = useState(p.clientName || "");
  const [sellingPrice, setSellingPrice] = useState(p.sellingPrice ? String(p.sellingPrice) : "");
  const [margin, setMargin] = useState(p.margin ? String(p.margin) : "");
  return (
    <div className="pn-card">
      <h3>Only you see this</h3>
      <div className="pn-form">
        <label className="is-wide">Client<input value={clientName} onChange={(e) => setClientName(e.target.value)} /></label>
        <label>Selling price (₹)<input inputMode="decimal" value={sellingPrice} onChange={(e) => setSellingPrice(e.target.value)} /></label>
        <label>Margin (%)<input inputMode="decimal" value={margin} onChange={(e) => setMargin(e.target.value)} /></label>
      </div>
      {!order.readOnly ? (
        <div className="pn-actions">
          <button className="kh-btn" disabled={busy} onClick={() => run(() => savePrivate(order.id, {
            clientName, sellingPrice: sellingPrice ? Number(sellingPrice) : null, margin: margin ? Number(margin) : null,
          }), "Saved")}>Save</button>
        </div>
      ) : null}
    </div>
  );
};

export default ProductionOrderPage;
